/**
 * The in-app bell and modal (FLIP-467). The server builds this from the notification log, so it is the
 * same on every device and survives reload and reinstall; the browser no longer schedules cards with
 * timers or keeps unseen state in localStorage. Mirrors NotificationBellResponse on the API side.
 */
export interface NotificationBell {
  /** The badge: the Score item (at most one), the Plan item (at most one), every Brain Science Bite item and every Community item. */
  total: number;
  /** The single Score entry, or null. Score reminders do not accumulate. */
  score: BellScoreItem | null;
  /** The single Plan entry (FLIP-474), or null. The newest replaces older ones; the server drops it once the user has a plan for that day. */
  plan: BellPlanItem | null;
  /** One item per distinct unseen bite, newest first. Bites accumulate. */
  bites: BellBiteItem[];
  /**
   * One item per Community event (FLIP-470), newest first. Community accumulates and is uncapped in v1. Always
   * empty while Community notifications are switched off on the server (FLIP-477).
   */
  community: BellCommunityItem[];
}

export interface BellScoreItem {
  /** Every unseen Score log row this item stands for; posted back to mark the item seen. */
  notificationLogIds: number[];
  receivedUtc: string;
}

export interface BellPlanItem {
  /** Every unseen Plan log row this item stands for (the newest replaced the rest); posted back to mark it seen. */
  notificationLogIds: number[];
  receivedUtc: string;
}

export interface BellBiteItem {
  /** Every unseen log row for this bite (the same bite can be announced on several days). */
  notificationLogIds: number[];
  biteId: number;
  /** The bite's notification text, or a generic line if it has none. */
  text: string;
  receivedUtc: string;
}

/**
 * One Community (NodeBB) event (FLIP-470). Unlike Score and Bite, which have fixed copy, each carries its own title,
 * body and link from the webhook payload, stored on the server's log row.
 */
export interface BellCommunityItem {
  /** The one log row this event is; posted back to mark it seen. */
  notificationLogIds: number[];
  title: string;
  body: string;
  /**
   * A same-origin relative path validated on the server (e.g. "/community?post=%2Ftopic%2F37"), or null when the
   * event has no link - the client then opens the Community landing page.
   */
  deepLink: string | null;
  receivedUtc: string;
}

/** The empty bell, used before the first load and after logout. */
export const EMPTY_NOTIFICATION_BELL: NotificationBell = { total: 0, score: null, plan: null, bites: [], community: [] };
